import { ReasonType } from "@rooster/types";
import axios from "axios";
import { v4 } from "uuid";
import { logRocketSessionUrl } from "../../utils/logRocket";
import { baseUrlFunctions, baseUrlServer } from "./common";
import { Participant } from "./types/availability.type";
import { RoosterLinkSchedulePayload } from "./types/schedule.roosterlink.type";

export const scheduleAutobookPrepare = async ({
  availabilityId,
  participants
}: {
  availabilityId: string;
  participants: Participant[];
}) => {
  const url = `${baseUrlFunctions}/autobookRequest/prepare-booking`;
  try {
    const response = await axios.post(
      url,
      {
        availabilityId,
        participants: participants.map((p) => p.value)
      },
      { timeout: 120000 }
    );
    if (response.status !== 200) {
      throw new Error("Failed to prepare autobook request");
    }
    return response.data as { status: string; msg: string };
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error?.message);
    }
  }
  return { status: "error", msg: "Failed to prepare autobook request" };
};

export const scheduleAutobook = async ({
  availabilityId,
  slot,
  timeZone,
  mode,
  hasDynamicFilters = false,
  reason,
  comment
}: {
  availabilityId: string;
  slot: {
    day: number;
    participants?: Participant[];
    slots: { hour: number; minute: number }[];
  };
  timeZone: string;
  mode: "schedule" | "reschedule";
  hasDynamicFilters?: boolean;
  reason?: ReasonType;
  comment?: string;
}) => {
  if (mode === "schedule" && hasDynamicFilters) {
    const { status } = await scheduleAutobookPrepare({
      availabilityId,
      participants: slot?.participants ?? []
    });
    if (status !== "ok") {
      console.error("Failed to prepare autobook request");
      return "FAILED";
    }
  }

  const url = `${baseUrlServer}/multiDayAutobook/candidate/${mode}`;
  try {
    const reasonPayload =
      mode === "reschedule"
        ? reason
          ? { ...reason, comment }
          : {
              audience: "candidate",
              type: "reschedule",
              scheduledEventType: "candidateApp",
              comment
            }
        : undefined;

    const response = await axios.put(
      url,
      {
        data: {
          id: availabilityId,
          ...(reasonPayload && { reason: reasonPayload }),
          slots: [slot],
          timeZone
        },
        config: {
          uuid: v4(),
          sessionUrl: logRocketSessionUrl ?? "",
          requestTimestamp: Date.now(),
          method: "autobook"
        }
      },
      { timeout: 120000 }
    );

    return response.data as string;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error?.message);
    }
  }
};

export const scheduleRoosterLink = async (
  personIdentifier: string,
  profileIdentifier: string,
  payload: RoosterLinkSchedulePayload
) => {
  const url = `${baseUrlFunctions}/personalMeetingLinks/v2/schedule?personIdentifier=${personIdentifier}&profileIdentifier=${profileIdentifier} `;
  try {
    const response = await axios.post(
      url,
      { ...payload, sessionUrl: logRocketSessionUrl ?? "" },
      { timeout: 120000 }
    );
    const json = (await response.data) as {
      availabilityId?: string;
      scheduled: boolean;
      errorCode?: string;
    };
    return json;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error(error.response?.data?.code);
      return {
        availabilityId: undefined,
        errorCode: (error.response?.data?.code as string) || "UNKNOWN_ERROR",
        scheduled: false
      };
    }
    return { errorCode: "UNKNOWN_ERROR", scheduled: false };
  }
};
